import { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { useUserMetrics } from '../contexts/UserMetricsContext';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, addDoc, onSnapshot, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { PiCrownSimpleFill } from "react-icons/pi";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const PricingToggle = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => `${theme.colors.secondary}15`};
  padding: 4px;
  border-radius: 24px;
  margin: ${({ theme }) => theme.spacing.lg} 0;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ToggleButton = styled.button`
  background: ${({ $active, theme }) => 
    $active 
      ? theme.colors.white 
      : `${theme.colors.secondary}20`};
  color: ${({ $active, theme }) => 
    $active 
      ? theme.colors.primary 
      : theme.colors.text};
  border: none;
  padding: 10px 24px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  font-size: 1rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ $active, theme }) => 
      $active 
        ? theme.colors.white 
        : `${theme.colors.secondary}30`};
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin: ${({ theme }) => theme.spacing.xl} 0;
`;

const shimmerBorder = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const shine = keyframes`
  0% {
    opacity: 0.5;
    transform: translateX(-100%);
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
`;

const PricingCard = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: 12px;
  transition: all 0.3s ease;
  overflow: hidden;
  
  ${({ $isPremium }) => $isPremium ? css`
    background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 12px;
      padding: 3px;
      background: linear-gradient(
        60deg,
        #ffd700,
        #f5cc00,
        #ffed4a,
        #fff1a6,
        #ffd700
      );
      background-size: 300% 300%;
      animation: ${shimmerBorder} 8s linear infinite;
      -webkit-mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
      z-index: 1;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.8),
        transparent
      );
      animation: ${shine} 4s infinite;
      pointer-events: none;
      z-index: 1;
    }

    h2 {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${({ theme }) => theme.spacing.sm};
      color: #8b6b0e;
      
      svg {
        width: 24px;
        height: 24px;
        color: #ffd700;
      }
    }

    ${Button} {
      position: relative;
      z-index: 2;
      cursor: pointer;

      &:not(:disabled):hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
    }

    ${FeatureList} {
      position: relative;
      z-index: 2;
    }

    ${Price} {
      position: relative;
      z-index: 2;
    }
  ` : css`
    background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.colors.white} 0%,
      ${({ theme }) => `${theme.colors.secondary}10`} 100%
    );
    border: 2px solid ${({ theme }) => `${theme.colors.secondary}40`};

    ${Button} {
      cursor: pointer;

      &:not(:disabled):hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
    }
  `}

  box-shadow: ${({ $isPremium }) => 
    $isPremium 
      ? '0 8px 32px rgba(255, 215, 0, 0.15)'
      : '0 4px 8px rgba(0, 0, 0, 0.05)'};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ $isPremium }) => 
      $isPremium 
        ? '0 12px 48px rgba(255, 215, 0, 0.2)'
        : '0 8px 16px rgba(0, 0, 0, 0.1)'};
  }
`;

const SaveBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.success};
  color: white;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 0 12px 0 12px;
  box-shadow: -2px 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 2;
`;

const Price = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  margin: ${({ theme }) => theme.spacing.lg} 0;
  color: ${({ theme }) => theme.colors.primary};

  span {
    font-size: 1rem;
    opacity: 0.7;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing.xl} 0;
  text-align: left;

  li {
    margin-bottom: ${({ theme }) => theme.spacing.md};
    &:before {
      content: "✓";
      color: ${({ theme }) => theme.colors.success};
      margin-right: ${({ theme }) => theme.spacing.sm};
    }
  }
`;

const Error = styled.div`
  color: ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => `${theme.colors.error}10`};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: 8px;
  margin: ${({ theme }) => theme.spacing.md} 0;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: 0
  }).format(price.unit_amount / 100);
};

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const { metrics } = useUserMetrics();
  const [products, setProducts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();
  
  // Load products and subscription status
  useEffect(() => {
    const loadProducts = async () => {
      const productsRef = collection(db, 'products');
      const productsQuery = query(productsRef, where('active', '==', true));
      const productsSnapshot = await getDocs(productsQuery);
      
      const productsData = [];
      
      for (const productDoc of productsSnapshot.docs) {
        const product = productDoc.data();
        const pricesRef = collection(productDoc.ref, 'prices');
        const pricesQuery = query(pricesRef, where('active', '==', true));
        const pricesSnapshot = await getDocs(pricesQuery);
        
        const prices = pricesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        productsData.push({
          id: productDoc.id,
          ...product,
          prices
        });
      }
      
      setProducts(productsData);
    };

    const loadSubscription = async () => {
      if (!user) return;
      
      const customerDoc = doc(db, 'customers', user.uid);
      const subscriptionsRef = collection(customerDoc, 'subscriptions');
      const subscriptionsQuery = query(subscriptionsRef, where('status', 'in', ['trialing', 'active']));
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
      
      if (!subscriptionsSnapshot.empty) {
        setSubscription(subscriptionsSnapshot.docs[0].data());
      }
    };

    Promise.all([loadProducts(), loadSubscription()])
      .finally(() => setLoading(false));
  }, [user]);

  const handleSubscribe = async (priceId) => {
    console.log('Subscribe clicked, auth state:', { user, isAuthenticated: !!user });
    
    if (!user) {
      console.log('No user found in AuthContext');
      setError('Please sign in to subscribe');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // First, check if the customer document exists
      const customersRef = collection(db, 'customers');
      const customerDoc = doc(customersRef, user.uid);
      console.log('Checking customer document:', customerDoc.path);

      // Create the checkout session
      console.log('Creating checkout session with price:', priceId);
      const checkoutRef = collection(customerDoc, 'checkout_sessions');
      const docRef = await addDoc(checkoutRef, {
        price: priceId,
        success_url: window.location.origin + '/dashboard?success=true',
        cancel_url: window.location.origin + '/pricing',
        allow_promotion_codes: true,
        tax_rates: [],
        mode: 'subscription',
        metadata: {
          userId: user.uid
        }
      });
      
      console.log('Checkout session document created:', docRef.id);
      
      // Wait for the CheckoutSession to get attached by the extension
      let unsubscribe = onSnapshot(docRef, (snap) => {
        const data = snap.data();
        console.log('Checkout session updated:', data);
        
        if (data?.error) {
          console.error('Checkout session error:', data.error);
          setError(`An error occurred: ${data.error.message}`);
          setLoading(false);
          unsubscribe();
        } else if (data?.url) {
          console.log('Redirecting to checkout URL:', data.url);
          window.location.assign(data.url);
        } else {
          console.log('Waiting for checkout session URL...');
        }
      }, (error) => {
        console.error('Snapshot error:', error);
        setError('An error occurred while creating the checkout session');
        setLoading(false);
      });

      // Set a timeout to prevent infinite loading
      setTimeout(() => {
        if (loading) {
          console.error('Checkout session timeout - no URL received');
          setError('Timeout while creating checkout session. Please try again.');
          setLoading(false);
          unsubscribe();
        }
      }, 10000); // 10 second timeout

    } catch (error) {
      console.error('Subscription error:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading...</LoadingSpinner>
      </Container>
    );
  }

  // Filter prices based on billing cycle
  const getFilteredPrices = (prices) => {
    return prices.filter(price => price.interval === billingCycle);
  };

  // Calculate savings for yearly plans
  const calculateSavings = (prices) => {
    const monthlyPrice = prices.find(p => p.interval === 'month')?.unit_amount || 0;
    const yearlyPrice = prices.find(p => p.interval === 'year')?.unit_amount || 0;
    
    if (monthlyPrice && yearlyPrice) {
      return ((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12) * 100).toFixed(0);
    }
    return 0;
  };

  return (
    <Container>
      <h1>Simple, Transparent Pricing</h1>
      <p>Choose the plan that works best for you</p>

      <PricingToggle>
        <ToggleButton 
          $active={billingCycle === 'month'} 
          onClick={() => setBillingCycle('month')}
        >
          Monthly
        </ToggleButton>
        <ToggleButton 
          $active={billingCycle === 'year'} 
          onClick={() => setBillingCycle('year')}
        >
          Yearly
        </ToggleButton>
      </PricingToggle>

      {error && <Error>{error}</Error>}

      <PricingGrid>
        {/* Free Plan */}
        <PricingCard>
          <h2>Free</h2>
          <Price>
            $0 <span>/forever</span>
          </Price>
          <FeatureList>
            <li>Up to 2 rooms</li>
            <li>Up to 5 bins per room</li>
            <li>100MB storage</li>
            <li>Basic features</li>
          </FeatureList>
          {user ? (
            <Button disabled={!subscription}>
              Current Plan
            </Button>
          ) : (
            <Button onClick={() => navigate('/signup')}>
              Start Free
            </Button>
          )}
        </PricingCard>

        {/* Premium Plans from Stripe */}
        {products.map(product => {
          const filteredPrices = getFilteredPrices(product.prices);
          const savings = billingCycle === 'year' ? calculateSavings(product.prices) : 0;
          
          return filteredPrices.map(price => (
            <PricingCard key={price.id} $isPremium>
              {savings > 0 && <SaveBadge>Save {savings}%</SaveBadge>}
              <h2>
                {product.name}
                <PiCrownSimpleFill />
              </h2>
              <Price>
                {formatPrice(price)} <span>/{price.interval}</span>
              </Price>
              <FeatureList>
                <li>Unlimited rooms</li>
                <li>Unlimited bins</li>
                <li>5GB photo storage</li>
                <li>Household sharing (Coming soon)</li>
                <li>Binsurance (Coming soon)</li>
                <li>Export to CSV (Coming soon)</li>
                <li>Export to PDF (Coming soon)</li>
                <li>AI Reorganizer (Coming soon)</li>
                {product.features?.map((feature, index) => (
                  <li key={index}>{feature} (Coming soon)</li>
                ))}
              </FeatureList>
              <Button
                onClick={() => handleSubscribe(price.id)}
                disabled={subscription}
              >
                {subscription ? 'Current Plan' : 'Subscribe Now'}
              </Button>
            </PricingCard>
          ));
        })}
      </PricingGrid>
    </Container>
  );
};

export default Pricing; 